import { useEffect, useState } from "react";
import { receiveManufacturingPurchaseOrder } from "@/lib/manufacturing/purchaseOrderClient";
import { poHasInvoiceRecord } from "@/lib/manufacturing/purchaseOrderInvoice";
import type { MfgPurchaseOrder } from "@/lib/manufacturing/purchaseOrderTypes";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

type ReceiveDraft = Record<
  string,
  {
    received_qty: string;
    quality_note: string;
    quality_status: "ok" | "off_quality";
    chargeback_usd: string;
    rate: string;
  }
>;

function emptyDraft(order: MfgPurchaseOrder): ReceiveDraft {
  const init: ReceiveDraft = {};
  for (const line of order.lines ?? []) {
    init[line.id] = {
      received_qty: line.received_qty != null ? String(line.received_qty) : String(line.quantity),
      quality_note: line.quality_note || "",
      quality_status: line.quality_status === "off_quality" ? "off_quality" : "ok",
      chargeback_usd: line.chargeback_usd == null ? "" : String(line.chargeback_usd),
      rate: line.rate == null ? "" : String(line.rate),
    };
  }
  return init;
}

export function PurchaseOrderDeliveryConfirm(props: {
  order: MfgPurchaseOrder;
  enabled: boolean;
  onUpdated: (order: MfgPurchaseOrder) => void;
  onReload: () => void;
  onToast: (msg: string, ok: boolean) => void;
}) {
  const { order } = props;
  const invoiced = poHasInvoiceRecord(order);
  const canReceive =
    invoiced &&
    (order.status === "issued" ||
      order.status === "confirmed" ||
      order.status === "partially_received");
  const [busy, setBusy] = useState(false);
  const [recv, setRecv] = useState<ReceiveDraft>(() => emptyDraft(order));
  const lineKey = (order.lines ?? []).map((line) => `${line.id}:${line.quantity}:${line.received_qty ?? ""}`).join("|");
  useEffect(() => {
    setRecv(emptyDraft(order));
  }, [order.id, lineKey]);

  async function submitReceive() {
    const receipts = (order.lines ?? [])
      .filter((l) => recv[l.id])
      .map((l) => {
        const d = recv[l.id];
        const received_qty = Number(d.received_qty);
        const rate = d.rate.trim() === "" ? undefined : Number(d.rate);
        const chargeback_usd = d.chargeback_usd.trim() === "" ? null : Number(d.chargeback_usd);
        return {
          line_id: l.id,
          received_qty,
          quality_note: d.quality_note.trim(),
          quality_status: d.quality_status,
          chargeback_usd,
          ...(rate != null && Number.isFinite(rate) ? { rate } : {}),
        };
      })
      .filter((r) => Number.isFinite(r.received_qty) && r.received_qty >= 0);

    if (receipts.length === 0) {
      props.onToast("Enter received quantities for at least one line.", false);
      return;
    }
    setBusy(true);
    const res = await receiveManufacturingPurchaseOrder({ po_id: order.id, receipts });
    setBusy(false);
    if (!res.ok) {
      props.onToast(res.error, false);
      return;
    }
    props.onToast(`Delivery confirmed on PO ${res.data.order.po_number}`, true);
    props.onUpdated(res.data.order);
  }

  if (!canReceive && !invoiced && order.status !== "draft" && order.status !== "cancelled") {
    return (
      <p className="text-[11px] text-amber-800 dark:text-amber-200">
        Record the vendor invoice above before confirming what landed.
      </p>
    );
  }
  if (!canReceive) return null;

  return (
    <div>
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">
        Confirm delivery
      </div>
      <p className="text-[11px] text-gray-500">
        On-the-ground check. Off-quality stays out of inventory and can take a chargeback.
      </p>
      <div className="mt-2 space-y-2">
        {(order.lines ?? []).map((line) => {
          const d = recv[line.id] ?? emptyDraft(order)[line.id];
          if (!d) return null;
          return (
            <div key={line.id} className="rounded-lg border border-gray-100 p-2 dark:border-zinc-800">
              <div className="text-xs font-medium">{line.material_name}</div>
              <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <label className="text-[11px]">
                  Received qty
                  <input
                    className={`${inputClass} mt-0.5 w-full`}
                    value={d.received_qty}
                    disabled={!props.enabled || busy}
                    onChange={(e) =>
                      setRecv((prev) => ({
                        ...prev,
                        [line.id]: { ...d, received_qty: e.target.value },
                      }))
                    }
                  />
                </label>
                <label className="text-[11px]">
                  Quality
                  <select
                    className={`${inputClass} mt-0.5 w-full`}
                    value={d.quality_status}
                    disabled={!props.enabled || busy}
                    onChange={(e) =>
                      setRecv((prev) => ({
                        ...prev,
                        [line.id]: {
                          ...d,
                          quality_status: e.target.value === "off_quality" ? "off_quality" : "ok",
                        },
                      }))
                    }
                  >
                    <option value="ok">Accept</option>
                    <option value="off_quality">Off quality</option>
                  </select>
                </label>
                <label className="text-[11px]">
                  Chargeback $
                  <input
                    className={`${inputClass} mt-0.5 w-full`}
                    value={d.chargeback_usd}
                    disabled={!props.enabled || busy}
                    onChange={(e) =>
                      setRecv((prev) => ({
                        ...prev,
                        [line.id]: { ...d, chargeback_usd: e.target.value },
                      }))
                    }
                  />
                </label>
                <label className="text-[11px]">
                  Confirmed rate
                  <input
                    className={`${inputClass} mt-0.5 w-full`}
                    value={d.rate}
                    disabled={!props.enabled || busy}
                    onChange={(e) =>
                      setRecv((prev) => ({
                        ...prev,
                        [line.id]: { ...d, rate: e.target.value },
                      }))
                    }
                  />
                </label>
                <label className="col-span-2 text-[11px] sm:col-span-4">
                  Quality / follow-up note
                  <input
                    className={`${inputClass} mt-0.5 w-full`}
                    value={d.quality_note}
                    disabled={!props.enabled || busy}
                    onChange={(e) =>
                      setRecv((prev) => ({
                        ...prev,
                        [line.id]: { ...d, quality_note: e.target.value },
                      }))
                    }
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={!props.enabled || busy}
          onClick={() => void submitReceive()}
        >
          Confirm delivery into inventory
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => props.onReload()}>
          Refresh
        </button>
      </div>
    </div>
  );
}
